/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	wrapper: {
		boxSizing: 'border-box',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
	},
	updatedWrapper: {
		columnGap: token('space.300'),
	},
	iconContainer: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	content: {
		display: 'flex',
		alignItems: 'center',
		columnGap: token('space.100'),
		flex: 1,
	},
	buttonGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

export type RovoThinkingReadyProps = {
	onView?: () => void;
	onClose: () => void;
	readyText?: string;
	/** Whether to render the ready-state check icon. Defaults to `true`. */
	showCheckIcon?: boolean;
};

export function RovoThinkingReady({
	onView,
	onClose,
	readyText,
	showCheckIcon = true,
}: RovoThinkingReadyProps): JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<div css={[styles.wrapper, fg('rovo_ready_bar_ui_update') && styles.updatedWrapper]}>
			<div css={styles.content}>
				{fg('rovo_ready_bar_ui_update') ? (
					showCheckIcon && (
						<div css={styles.iconContainer}>
							<CheckMarkIcon
								label=""
								size="medium"
								color={token('color.icon.success')}
								testId="thinking-ready-check-icon"
							/>
						</div>
					)
				) : (
					<div css={styles.iconContainer}>
						<CheckCircleIcon
							label=""
							color={token('color.icon.success')}
							testId="thinking-ready-check-circle-icon"
						/>
					</div>
				)}
				<Text>{readyText ?? formatMessage(messages.thinkingReadyMessage)}</Text>
			</div>
			<div css={styles.buttonGroup}>
				{fg('rovo_ready_bar_ui_update') ? (
					<Fragment>
						{onView && (
							<Fragment>
								<Button
									appearance="subtle"
									spacing="default"
									onClick={onClose}
									testId="thinking-ready-close-button"
								>
									{formatMessage(messages.thinkingReadyClose)}
								</Button>
								<Button appearance="primary" onClick={onView} testId="thinking-ready-view-button">
									{formatMessage(messages.thinkingReadyView)}
								</Button>
							</Fragment>
						)}
						{!onView && (
							<IconButton
								appearance="subtle"
								spacing={fg('rovo_ready_bar_fix_height') ? 'default' : 'compact'}
								onClick={onClose}
								testId="thinking-ready-close-button"
								label={formatMessage(messages.thinkingReadyClose)}
								icon={CrossIcon}
							/>
						)}
					</Fragment>
				) : (
					<Fragment>
						{onView && (
							<Button appearance="primary" onClick={onView} testId="thinking-ready-view-button">
								{formatMessage(messages.thinkingReadyView)}
							</Button>
						)}
						<IconButton
							appearance="subtle"
							spacing={!!onView || !fg('rovo_ready_bar_fix_height') ? 'compact' : 'default'}
							onClick={onClose}
							testId="thinking-ready-close-button"
							label={formatMessage(messages.thinkingReadyClose)}
							icon={CrossIcon}
						/>
					</Fragment>
				)}
			</div>
		</div>
	);
}
